import React, { useState, useEffect, useRef } from 'react';
import images from '../../images';
import {getUserIdFromStorage} from '../../api/auth.sso';
import { apiRequest } from '../../api/index';
import ToastUtils from '../../utils/toast';
import DropdownMenu from '../DropdownMenu';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const IntegrationModal = ({ isOpen, onClose, onIntegrationCreated, integration = null, userData }) => {

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [availableParameters, setAvailableParameters] = useState([
    { id: 'param_comments', name: 'Комментарий', value: '' },
    { id: 'param_utm_medium', name: 'UTM_MEDIUM', value: '' },
    { id: 'param_utm_campaign', name: 'UTM_CAMPAIGN', value: '' },
    { id: 'param_utm_term', name: 'UTM_TERM', value: '' },
    { id: 'param_utm_content', name: 'UTM_CONTENT', value: '' },
    { id: 'param_region', name: 'Регион', value: '' },
    { id: 'param_title', name: 'Title', value: '' },
    { id: 'param_other', name: 'Другое', value: '' }
  ]);
  const [selectedParameters, setSelectedParameters] = useState([
    { id: 'param_phone', name: 'Телефон', value: '' },
    { id: 'param_utm_source', name: 'UTM_SOURCE', value: '' },
    { id: 'param_city', name: 'Город', value: '' },
    { id: 'param_name', name: 'Имя', value: '' },
    { id: 'param_debt', name: 'Debt', value: '' },
  ]);
  const [selectedFlows, setSelectedFlows] = useState([]);
  const [userID, setUserID] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [documentationFile, setDocumentationFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [removedParameters, setRemovedParameters] = useState([]);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [name, setName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [showValidationTooltip, setShowValidationTooltip] = useState(false);

  const tooltipRef = useRef(null);

  // Tooltip ID для обязательных полей
  const validationTooltipId = 'required-field-tooltip';

  // Функция для отображения tooltip о не заполненности
  const requiredFieldTooltip = (
    <Tooltip id={validationTooltipId}>
      Это поле обязательно для заполнения
    </Tooltip>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowHelpTooltip(false);
      }
    };

    if (showHelpTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpTooltip]);

  useEffect(() => {

    const loadUserData = async () => {
      try {
        // Получаем ID пользователя из userData или из localStorage
        const userId = userData?.id || getUserIdFromStorage();
        console.log('[IntegrationModal] Using userId:', userId, 'userData:', userData);
        setUserID(userId);
        // Получаем потоки пользователя по его ID
      } catch (error) {
        // Проверяем, что компонент все еще смонтирован перед обновлением состояния
        console.error('Ошибка при загрузке потоков:', error);
      }
    };

    if (isOpen) {
      loadUserData();
    }

  }, [isOpen]);

  // Маппинг ключей без param_ к ключам с param_
  const mapParamKeyToFullKey = (key) => {
    const keyMap = {
      'name': 'param_name',
      'city': 'param_city',
      'comments': 'param_comments',
      'utm_medium': 'param_utm_medium',
      'utm_campaign': 'param_utm_campaign',
      'utm_term': 'param_utm_term',
      'utm_content': 'param_utm_content',
      'debt': 'param_debt',
      'region': 'param_region',
      'title': 'param_title',
      'other': 'param_other',
      'phone': 'param_phone',
      'utm_source': 'param_utm_source',
    };
    return keyMap[key] || `param_${key}`;
  };

  // Инициализация данных при редактировании интеграции
  useEffect(() => {
    if (isOpen && integration) {
      setIsEditMode(true);
      if (integration.url) setWebhookUrl(integration.url);
      if (integration.name) setName(integration.name || '');
      setEditNameValue(integration.name || '');

      // Обработка дополнительных параметров - обязательные параметры всегда присутствуют
      const params = [];

      // Добавляем обязательные параметры с их значениями из интеграции
      if (integration.param) {
        // Сначала добавляем обязательные параметры (без префикса param_)
        if (integration.param.phone !== undefined && integration.param.phone !== null) {
          params.push({ id: 'param_phone', name: 'Телефон', value: integration.param.phone });
        } else {
          params.push({ id: 'param_phone', name: 'Телефон', value: '' });
        }

        if (integration.param.utm_source !== undefined && integration.param.utm_source !== null) {
          params.push({ id: 'param_utm_source', name: 'UTM_SOURCE', value: integration.param.utm_source });
        } else {
          params.push({ id: 'param_utm_source', name: 'UTM_SOURCE', value: '' });
        }

        if (integration.param.city !== undefined && integration.param.city !== null) {
          params.push({ id: 'param_city', name: 'Город', value: integration.param.city });
        } else {
          params.push({ id: 'param_city', name: 'Город', value: '' });
        }

        if (integration.param.name !== undefined && integration.param.name !== null) {
          params.push({ id: 'param_name', name: 'Имя', value: integration.param.name });
        } else {
          params.push({ id: 'param_name', name: 'Имя', value: '' });
        }

        if (integration.param.debt !== undefined && integration.param.debt !== null) {
          params.push({ id: 'param_debt', name: 'Debt', value: integration.param.debt });
        } else {
          params.push({ id: 'param_debt', name: 'Debt', value: '' });
        }

        // Добавляем дополнительные параметры
        Object.entries(integration.param).forEach(([key, value]) => {
          // Пропускаем обязательные параметры и name
          if (['phone', 'utm_source', 'city', 'name', 'debt'].includes(key)) return;

          // Добавляем параметр, только если значение не null и не undefined
          if (value !== null && value !== undefined) {
            const fullKey = mapParamKeyToFullKey(key);
            const paramNameObj = availableParameters.find(p => p.id === fullKey);
            const paramName = paramNameObj ? paramNameObj.name : key;
            params.push({ id: fullKey, name: paramName, value: value });
          }
        });
      } else {
        // Если param не передан, добавляем пустые обязательные параметры
        params.push({ id: 'param_phone', name: 'Телефон', value: '' });
        params.push({ id: 'param_utm_source', name: 'UTM_SOURCE', value: '' });
        params.push({ id: 'param_city', name: 'Город', value: '' });
        params.push({ id: 'param_name', name: 'Имя', value: '' });
        params.push({ id: 'param_debt', name: 'Debt', value: '' });
      }

      console.log('IntegrationModal: Setting parameters:', params);
      setSelectedParameters(params);

      // Если есть список потоков в интеграции
      if (integration.flows && Array.isArray(integration.flows)) {
        setSelectedFlows(integration.flows.map(flow => flow.flow_guide_id));
      }
    } else if (isOpen && !integration) {
      setIsEditMode(false);
      setName('');
      setEditNameValue('');
    }
  }, [isOpen, integration, integration?.id]);

  // Сброс состояния при закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      setRemovedParameters([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleNameSave = () => {
    setName(editNameValue);
    setIsEditingName(false);
  };

  const handleParameterSelect = (parameter) => {
    // Проверяем, что параметр еще не выбран
    if (!selectedParameters.some(p => p.id === parameter.id)) {
      setSelectedParameters([...selectedParameters, parameter]);
    }
  };

  const handleRemoveParameter = (parameterId) => {
    // Запрещаем удаление обязательных параметров
    if (['param_phone', 'param_utm_source', 'param_city', 'param_name', 'param_debt'].includes(parameterId)) return;
    // Добавляем параметр в список удаленных и удаляем из выбранных
    if (!removedParameters.includes(parameterId)) {
      setRemovedParameters([...removedParameters, parameterId]);
    }
    setSelectedParameters(selectedParameters.filter(p => p.id !== parameterId));
  };

  // Обработчик выбора параметра для редактирования
  const handleEditParameterSelect = (selectedParameter, index) => {
    const updatedParameters = [...selectedParameters];
    updatedParameters[index] = {
      ...updatedParameters[index],
      id: selectedParameter.id,
      name: selectedParameter.name
    };
    setSelectedParameters(updatedParameters);
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await apiRequest(`files/${fileId}/download`, 'GET', null, true);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Проверяем расширение файла
        const isPDF = fileName.toLowerCase().endsWith('.pdf');

        if (isPDF) {
          // Для PDF скачиваем файл
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          // Для других форматов открываем в новой вкладке
          window.open(url, '_blank');
        }

        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      ToastUtils.error('Ошибка при скачивании файла');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidationTooltip(true);

    // Проверяем обязательные поля
    const missingFields = [];

    if (!name || !name.trim()) {
      missingFields.push('Имя интеграции');
    }

    if (!webhookUrl || !webhookUrl.trim()) {
      missingFields.push('Вебхук');
    } else if (!webhookUrl.startsWith('http://') && !webhookUrl.startsWith('https://')) {
      ToastUtils.error('URL должен начинаться с http:// или https://');
      return;
    }

    if (!documentationFile) {
      missingFields.push('Файл документации');
    }

    selectedParameters.forEach(param => {
      if (!param.value) {
        missingFields.push(param.name);
      }
    });

    if (missingFields.length > 0) {
      ToastUtils.error(`Пожалуйста, заполните обязательные поля: ${missingFields.join(', ')}`);
      return;
    }

    if (isEditMode && !integration?.id) {
      ToastUtils.error('Ошибка: Не определен ID интеграции');
      return;
    }

    setIsSubmitting(true);

    try {
      // Создаем FormData для отправки файлов
      const formData = new FormData();

      // Добавляем обязательные параметры
      formData.append('url', webhookUrl);
      if (isEditMode){
        formData.append('id', integration.id);
      }
      formData.append('name', name);
      // Добавляем дополнительные параметры
      selectedParameters.forEach(param => {
        // Отправляем все значения, включая null (для удаленных параметров)
        if (param.value === null || param.value === '') {
          formData.append(param.id, 'null');
        } else if (param.value !== '') {
          formData.append(param.id, param.value);
        }
      });

      // Для режима редактирования: добавляем null для удаленных параметров
      if (isEditMode && removedParameters.length > 0) {
        removedParameters.forEach(paramId => {
          formData.append(paramId, 'null');
        });
      }
      // Добавляем ID потоков
      if (selectedFlows.length > 0) {
        selectedFlows.forEach(flowId => {
          formData.append('flow_ids[]', flowId);
        });
      }

      // Добавляем файл документации, если он выбран
      if (documentationFile) {
        formData.append('documentation_file', documentationFile);
      }

      // Отправляем запрос на создание или обновление интеграции
      const endpoint = isEditMode
        ? `integrations/${integration.id}`
        : `integrations/${userID}/add`;
      const method = isEditMode ? 'PATCH' : 'POST';

      const response = await apiRequest(endpoint, method, formData);

      if (response.message === "success") {
        ToastUtils.success(isEditMode ? 'Интеграция успешно обновлена' : 'Интеграция успешно создана');
        // Перезагружаем данные профиля через callback
        if (onIntegrationCreated) {
          await onIntegrationCreated();
        }
        onClose();
      } else {
        console.error('IntegrationModal: Response error:', response);
      }
    } catch (error) {
      console.error('IntegrationModal: Error:', error);
      ToastUtils.error(isEditMode ? 'Ошибка при обновлении интеграции' : 'Ошибка при создании интеграции');
    } finally {
      setIsSubmitting(false);
    }
  };
  console.log("integration: ", integration)
  return (
    <div className={isMobile ? "modal-overlay-mobile" : "modal-overlay"} onClick={onClose}>
      <div className="modal fullscreen bottom-right open" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isMobile && (
              <button className="modal-close-btn" onClick={onClose} aria-label="Закрыть">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {isEditingName ? (
                <input
                    type="text"
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    onBlur={handleNameSave}
                    autoFocus
                />
            ) : (
                <OverlayTrigger
                  placement="right"
                  overlay={requiredFieldTooltip}
                  show={showValidationTooltip && (!name || !name.trim())}
                  trigger={['hover', 'focus']}
                >
                  <span
                    className="integration-name-display"
                    onClick={() => setIsEditingName(true)}
                  >
                    {name || 'Без названия'}
                  </span>
                </OverlayTrigger>
            )}
            <button
                className="integration-name-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingName(true);
                }}
            >
              <img src={images.penLinear} alt="edit" className="advertiser-edit-icon"/>
            </button>
          </h3>
        </div>
        <div className="modal-body">
          <div className="integration-modal-upper">
            <div className="integration-webhook">
              <h4>Вебхук *</h4>
              <div className="webhook-input-container">
                <img
                    src={images.cloud}
                    alt=""
                    className="webhook-icon"
                />
                <input
                    type="text"
                    placeholder="https://app.diagrams.net/?src=about"
                    className={`webhook-input ${showValidationTooltip && !webhookUrl ? 'is-invalid' : ''}`}
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              {showValidationTooltip && !webhookUrl && (
                <OverlayTrigger
                  placement="right"
                  overlay={requiredFieldTooltip}
                  trigger={['hover', 'focus']}
                >
                  <span className="invalid-feedback">Это поле обязательно для заполнения</span>
                </OverlayTrigger>
              )}
            </div>
          </div>

          <div className="integration-parameters">
            <div className="integration-parameters-header">
            <h4>Параметры *</h4>
              <div className="help-icon-container" onClick={() => setShowHelpTooltip(!showHelpTooltip)}>
                <img
                  src={images.questionActive}
                  alt="Справка"
                  className="help-icon"
                />
                {showHelpTooltip && (
                  <div className="help-tooltip" ref={tooltipRef}>
                    <h5 className="help-tooltip-title">Как работать с параметрами?</h5>
                    <div className="help-tooltip-content">
                      <p>1. В левой колонке выберите параметр, который хотите получить.</p>
                      <p>2. В правой колонке укажите параметр, в который нужно записывать значение на вашем портале.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="parameters-container">
              {selectedParameters.map((parameter, index) => (
                <div key={parameter.id} className="selected-parameter-group">
                  <div className="dropdown-input-container">
                    <DropdownMenu
                      trigger={parameter.name}
                      options={availableParameters.map(availableParam => ({
                        id: availableParam.id,
                        name: availableParam.name,
                        disabled: selectedParameters.some(p => p.id === availableParam.id && p.id !== parameter.id)
                      }))}
                      classNameToggle={"dropdown-toggle"}
                      onSelect={(option) => handleEditParameterSelect(option, index)}
                      disabledOptions={selectedParameters.filter(p => p.id === parameter.id).map(p => p.id)}
                    />
                  </div>
                  <img
                    src={images.doubleArrowDownLinear}
                    alt=""
                    className="parameter-separator"
                  />
                  <div className="input-with-remove">
                    <input
                      type="text"
                      placeholder={parameter.name}
                      className={`parameter-input ${showValidationTooltip && !parameter.value ? 'is-invalid' : ''}`}
                      value={parameter.value || ''}
                      onChange={(e) => {
                        const updatedParameters = [...selectedParameters];
                        updatedParameters[index].value = e.target.value;
                        setSelectedParameters(updatedParameters);
                      }}
                    />
                    {/* Крестик для удаления необязательных параметров */}
                    {!['param_phone', 'param_utm_source', 'param_city', 'param_name', 'param_debt'].includes(parameter.id) && (
                      <button
                        type="button"
                        className="remove-parameter-btn"
                        onClick={() => handleRemoveParameter(parameter.id)}
                        title="Удалить параметр"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L11 11M11 1L1 11" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="add-parameter-button-container">
                <DropdownMenu
                  trigger={"Выбрать параметр"}
                  options={availableParameters.map(parameter => ({
                    id: parameter.id,
                    name: parameter.name,
                    disabled: selectedParameters.some(p => p.id === parameter.id)
                  }))}
                  className={"dropdown"}
                  classNameToggle={"btn btn-secondary dropdown-toggle"}
                  onSelect={(option) => handleParameterSelect(option)}
                />
              </div>
            </div>
          </div>
          <div className="integration-documentation">
            <h4>Документация *</h4>
            { integration && integration.file ?
              (
                <div className="webhook-input-container"
                      onClick={() =>handleDownloadFile(integration.file.id, integration.file.name)}>
                  <img
                    src={images.downloadLinear}
                    alt=""
                    className="webhook-icon"
                  />
                  <label htmlFor="documentation-file" className="documentation-file-label" style={{width: '100%', margin: 0}}>
                    <button
                      className="documentation-file-input"
                      onClick={() =>handleDownloadFile(integration.file.id, integration.file.name)}
                      style={{display: 'none'}}
                    />
                    {integration.file.name}
                  </label>
                </div>
              ) :
                (
                    <div className="webhook-input-container">
                      <img
                          src={images.paperclip}
                          alt=""
                          className="webhook-icon"
                      />
                      <label htmlFor="documentation-file" className={`documentation-file-label ${showValidationTooltip && !documentationFile ? 'is-invalid' : ''}`}
                             style={{width: '100%', margin: 0}}>
                        <input
                            type="file"
                            id="documentation-file"
                            className="documentation-file-input"
                            accept=".pdf,.docx,.doc"
                            onChange={(e) => setDocumentationFile(e.target.files[0])}
                            style={{display: 'none'}}
                        />
                        {documentationFile ? documentationFile.name : 'Выберите файл'}
                      </label>
                    </div>
                )
            }
            {showValidationTooltip && !documentationFile && (
              <div className="invalid-feedback d-block">Это поле обязательно для заполнения</div>
            )}

          </div>
        </div>
        <div className="modal-footer">
          <button
              className="btn btn-primary-filled"
              onClick={handleSubmit}
              disabled={isSubmitting}
          >
            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationModal;
